import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import ProfileHeader from './components/ProfileHeader';
import StatsOverview from './components/StatsOverview';
import PersonalInfoSection from './components/PersonalInfoSection';
import FitnessPreferencesSection from './components/FitnessPreferencesSection';
import MembershipSection from './components/MembershipSection';
import PrivacySettingsSection from './components/PrivacySettingsSection';
import AchievementsSection from './components/AchievementsSection';
import SecuritySection from './components/SecuritySection';
import IntegrationsSection from './components/IntegrationsSection';

const MemberProfile = () => {
  const [profile] = useState({
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    phone: "+1 (555) 123-4567",
    dateOfBirth: "1992-05-15",
    gender: "Female",
    address: "123 Fitness Street",
    city: "Los Angeles",
    state: "California",
    zipCode: "90001",
    emergencyContact: "John Johnson",
    emergencyPhone: "+1 (555) 987-6543",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_188a52a60-1763299337808.png",
    avatarAlt: "Professional fitness portrait of athletic woman with long brown hair wearing blue workout attire smiling confidently in modern gym setting",
    memberSince: "January 2024",
    membershipType: "Premium",
    streakDays: 45
  });

  const [stats] = useState([
  {
    icon: "Dumbbell",
    iconColor: "var(--color-accent)",
    bgColor: "bg-accent/10",
    value: "156",
    label: "Total Workouts",
    trend: 12
  },
  {
    icon: "Flame",
    iconColor: "var(--color-error)",
    bgColor: "bg-error/10",
    value: "45",
    label: "Day Streak",
    trend: 8
  },
  {
    icon: "Award",
    iconColor: "var(--color-warning)",
    bgColor: "bg-warning/10",
    value: "23",
    label: "Achievements",
    trend: 15
  },
  {
    icon: "TrendingUp",
    iconColor: "var(--color-success)",
    bgColor: "bg-success/10",
    value: "87%",
    label: "Goal Progress",
    trend: 5
  }]
  );

  const [preferences] = useState({
    workoutTypes: ['strength', 'cardio', 'yoga'],
    difficultyLevel: 'intermediate',
    workoutDuration: '45-60',
    fitnessGoals: ['Build Muscle', 'Improve Endurance', 'Increase Flexibility'],
    preferredDays: ['Monday', 'Wednesday', 'Friday'],
    preferredTime: 'morning'
  });

  const [membership] = useState({
    planName: "Premium Membership",
    price: 79.99,
    nextBillingDate: "February 15, 2026",
    memberSince: "January 15, 2024",
    autoRenewal: true,
    benefits: [
    "Unlimited gym access",
    "All group fitness classes",
    "Personal training sessions (4/month)",
    "Nutrition consultation",
    "Guest passes (2/month)",
    "Premium locker access"],

    paymentMethod: {
      last4: "4242",
      expiry: "12/27"
    }
  });

  const [privacySettings] = useState({
    profileVisibility: true,
    showWorkoutStats: true,
    showAchievements: true,
    allowMessages: true,
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    workoutReminders: true,
    classReminders: true,
    achievementAlerts: true,
    marketingEmails: false
  });

  const [achievements] = useState([
  {
    icon: "Trophy",
    name: "First Workout",
    description: "Complete your first workout",
    unlocked: true,
    unlockedDate: "Jan 15, 2024"
  },
  {
    icon: "Flame",
    name: "7 Day Streak",
    description: "Workout 7 days in a row",
    unlocked: true,
    unlockedDate: "Jan 22, 2024"
  },
  {
    icon: "Target",
    name: "Goal Crusher",
    description: "Achieve your first fitness goal",
    unlocked: true,
    unlockedDate: "Feb 10, 2024"
  },
  {
    icon: "Users",
    name: "Social Butterfly",
    description: "Attend 10 group classes",
    unlocked: true,
    unlockedDate: "Mar 5, 2024"
  },
  {
    icon: "Zap",
    name: "Power Lifter",
    description: "Complete 50 strength workouts",
    unlocked: false,
    unlockedDate: null
  },
  {
    icon: "Heart",
    name: "Cardio King",
    description: "Complete 50 cardio sessions",
    unlocked: false,
    unlockedDate: null
  },
  {
    icon: "Star",
    name: "100 Club",
    description: "Complete 100 total workouts",
    unlocked: false,
    unlockedDate: null
  },
  {
    icon: "Award",
    name: "Year Warrior",
    description: "Maintain membership for 1 year",
    unlocked: false,
    unlockedDate: null
  }]
  );

  const [security] = useState({
    lastPasswordChange: "December 10, 2025",
    twoFactorEnabled: true,
    loginHistory: [
    {
      device: "Desktop",
      location: "Los Angeles, CA",
      date: "Jan 23, 2026 at 8:30 AM"
    },
    {
      device: "Mobile",
      location: "Los Angeles, CA",
      date: "Jan 22, 2026 at 6:15 PM"
    },
    {
      device: "Desktop",
      location: "Los Angeles, CA",
      date: "Jan 21, 2026 at 9:00 AM"
    }]

  });

  const [integrations] = useState([
  {
    id: 'apple-health',
    name: "Apple Health",
    description: "Sync workouts and health data",
    icon: "/assets/images/More_Than_Technology_logo-01-1769218379125.jpg",
    connected: true,
    lastSync: "2 hours ago"
  },
  {
    id: 'fitbit',
    name: "Fitbit",
    description: "Track steps and heart rate",
    icon: "Watch",
    connected: true,
    lastSync: "1 day ago"
  },
  {
    id: 'strava',
    name: "Strava",
    description: "Share running and cycling activities",
    icon: "Bike",
    connected: false,
    lastSync: null
  },
  {
    id: 'myfitnesspal',
    name: "MyFitnessPal",
    description: "Sync nutrition and calorie data",
    icon: "Utensils",
    connected: false,
    lastSync: null
  }]
  );

  const handleEditPhoto = () => {
    console.log('Edit photo clicked');
  };

  const handleEditProfile = () => {
    console.log('Edit profile clicked');
  };

  const handleSavePersonalInfo = (data) => {
    console.log('Save personal info:', data);
  };

  const handleSavePreferences = (data) => {
    console.log('Save preferences:', data);
  };

  const handleUpgradePlan = () => {
    console.log('Upgrade plan clicked');
  };

  const handleManageBilling = () => {
    console.log('Manage billing clicked');
  };

  const handleSavePrivacySettings = (data) => {
    console.log('Save privacy settings:', data);
  };

  const handleShareAchievements = () => {
    console.log('Share achievements clicked');
  };

  const handleUpdatePassword = (data) => {
    console.log('Update password:', data);
  };

  const handleToggle2FA = (enabled) => {
    console.log('Toggle 2FA:', enabled);
  };

  const handleConnectIntegration = (id) => {
    console.log('Connect integration:', id);
  };

  const handleDisconnectIntegration = (id) => {
    console.log('Disconnect integration:', id);
  };

  return (
    <>
      <Helmet>
        <title>Member Profile - FitWellness</title>
        <meta name="description" content="Manage your FitWellness profile, preferences, and account settings" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12 pb-24 lg:pb-12">
          <div className="space-y-6 md:space-y-8 lg:space-y-10">
            <ProfileHeader
              profile={profile}
              onEditPhoto={handleEditPhoto}
              onEditProfile={handleEditProfile} />


            <StatsOverview stats={stats} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <PersonalInfoSection
                profile={profile}
                onSave={handleSavePersonalInfo} />


              <FitnessPreferencesSection
                preferences={preferences}
                onSave={handleSavePreferences} />

            </div>

            <MembershipSection
              membership={membership}
              onUpgrade={handleUpgradePlan}
              onManageBilling={handleManageBilling} />


            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <PrivacySettingsSection
                settings={privacySettings}
                onSave={handleSavePrivacySettings} />


              <SecuritySection
                security={security}
                onUpdatePassword={handleUpdatePassword}
                onToggle2FA={handleToggle2FA} />

            </div>

            <AchievementsSection
              achievements={achievements}
              onShare={handleShareAchievements} />


            <IntegrationsSection
              integrations={integrations}
              onConnect={handleConnectIntegration}
              onDisconnect={handleDisconnectIntegration} />

          </div>
        </main>

        <MobileBottomNav />
      </div>
    </>);

};

export default MemberProfile;